/**
 * Every ceiling and time budget for the custom-lexicon feature (VE-24948 / U5),
 * in one place.
 *
 * Two reasons this is a module rather than literals at call sites:
 *
 * 1. The size ceilings are enforced TWICE — here in the client, and again in the
 *    published JSON Schema for the `lexicon` data registry (NFR-S8). If the two
 *    drift, a user hits an opaque platform rejection instead of an inline error.
 *    When you change a value here, change the registry schema too — see
 *    the "Schema body" section of `docs/schemas/LexiconSchema.md`.
 * 2. The three MATCH budgets are a deliberate hierarchy, not three independent
 *    knobs. See `MATCH_BUDGET_*` below.
 * 3. The REGEX_PROBE_* values define the regex safety gate itself, and they are
 *    calibrated against the match budgets they protect. There is deliberately
 *    no structural complexity ceiling any more — see the note above them for
 *    why the one that used to live here was deleted rather than corrected.
 */

/** Max characters in a lexicon name (BR-1). */
export const LEXICON_NAME_MAX_LENGTH = 120;

/** Max characters in an entry's `term` / regex source (BR-12). */
export const ENTRY_TERM_MAX_LENGTH = 500;

/**
 * Max characters in a regex entry's source (BR-11, rule R7). Deliberately much
 * tighter than `ENTRY_TERM_MAX_LENGTH`: a literal term can legitimately be a
 * long phrase, but a 500-character regex is unreviewable by the person who has
 * to approve it.
 */
export const ENTRY_REGEX_MAX_LENGTH = 200;

/** Max entries in one lexicon (BR-16). Mirrored as `maxItems` in the schema. */
export const LEXICON_MAX_ENTRIES = 5000;

/** Max CSV upload size in bytes (BR-27). Checked BEFORE parsing. */
export const CSV_MAX_BYTES = 1024 * 1024;

/** Max bounded-repetition count allowed in a regex (rule R5). */
export const REGEX_MAX_REPEAT_COUNT = 100;

/** Max total quantifiers allowed in one regex (rule R6). */
export const REGEX_MAX_QUANTIFIERS = 10;

// ---------------------------------------------------------------------------
// Regex performance probe (ADR-001 as amended by review gate #3).
//
// THERE IS NO STRUCTURAL COMPLEXITY CEILING ANY MORE, AND THAT IS THE POINT.
// `REGEX_MAX_ADJACENT_AMBIGUOUS_QUANTIFIERS` used to live here with a
// measurement table behind it. It was rebuilt three times and bypassed three
// times — the last version accepted `(\w)*` x10 at 133,679 ms on a
// 40-character input while REJECTING `(?:ab)+`. The reason was always the same:
// deciding safety by inspecting a pattern requires a full parse of a language
// the screen only approximated, so each gap became an unsafe default one way
// and a false rejection the other.
//
// So the ceiling is now empirical. `regexProbeClient` runs the candidate
// against `regexProbeCorpus` inputs in a disposable worker and rejects on
// MEASURED time. The screen keeps only rules that are certain from syntax
// alone (see `regexScreen.ts`).
//
// The old calibration's fatal error is fixed at the root: it topped out at 120
// characters, and for polynomial backtracking input length dominates pattern
// shape (`[0-Z]*\W*` x4 on 60 characters cost 419 s — worse than x5 on 40).
// The probe therefore measures at page scale by construction.
// ---------------------------------------------------------------------------

/**
 * Length of each adversarial probe input, in characters.
 *
 * Thousands, not hundreds. This is the single number that made three previous
 * calibrations wrong; a document page is this long, so the probe must be too.
 */
export const REGEX_PROBE_INPUT_LENGTH = 3000;

/**
 * Max inputs the probe will try.
 *
 * The probe is a save-time gate, so its own cost is a product decision. A
 * linear pattern crosses this many page-scale inputs in single-digit
 * milliseconds; a pathological one blows `REGEX_PROBE_BUDGET_MS` on the first.
 */
export const REGEX_PROBE_MAX_INPUTS = 10;

/**
 * Measured wall-clock ceiling for the whole probe corpus, in ms.
 *
 * Sized against the runtime tier it protects: `MATCH_BUDGET_ENTRY_PAGE_MS` is
 * 50 ms for one entry against one page, so a pattern allowed to save must be
 * comfortably inside that per page. `REGEX_PROBE_MAX_INPUTS` page-scale inputs
 * at well under 50 ms each is the same statement, with headroom for a slower
 * machine than the author's.
 */
export const REGEX_PROBE_BUDGET_MS = 250;

/**
 * Hard deadline after which the probe worker is terminated, in ms.
 *
 * Deliberately larger than `REGEX_PROBE_BUDGET_MS`: a merely slow pattern
 * should REPORT its own time so the rejection can say what was measured. This
 * deadline exists for the pattern that never returns from a single `exec` —
 * which cooperative yielding cannot interrupt, so `terminate()` is the only
 * mechanism (ADR-002). Reaching it is a rejection, not a retry.
 */
export const REGEX_PROBE_DEADLINE_MS = 2000;

// ---------------------------------------------------------------------------
// Match budgets (ADR-001). Three tiers, and the middle one is load-bearing.
//
// At the NFR-U5-7 ceiling an apply is 5,000 entries x N pages. A per-page cap
// alone bounds each step while permitting an unbounded total; a total cap alone
// cannot attribute an overrun to one entry, which BR-47 requires ("name the
// failing entry"). So:
//
//   per entry-page      -> keeps a single evaluation imperceptible, and is
//                          enforced by TERMINATING the worker, so it bounds a
//                          page's cost whatever the pattern does
//   per entry MEAN page -> stops re-running a known-expensive entry on every
//                          remaining page, while still naming it. This is the
//                          tier that actually protects NFR-U5-6.
//   whole apply         -> backstop on total work
//
// THE MIDDLE TIER WAS A TOTAL AND IS NOW A RATE. A total is a budget on how
// long an entry has existed, which grows with the document, so a healthy
// literal term died partway through a long file and every re-evaluation died
// in the same place. See `MATCH_BUDGET_ENTRY_MEAN_PAGE_MS` for the measurement
// that forced the change, and for why bounding total work was never this
// tier's job.
//
// Tiers 1 and 3 remain engineering judgements rather than measurements.
// Revisit against the NFR-U5-6 harness (500 entries x 50 pages, <= 5s p95,
// first results <= 1s) — and note that harness is 50 pages, which is why the
// old tier 2 looked fine: a 1,602-page document is 32x it.
// ---------------------------------------------------------------------------

/**
 * Wall-clock budget for one entry against one page, in ms.
 *
 * The CLIENT multiplies this by the active entry count to get a page's budget,
 * but caps that product (`DEADLINE_ENTRY_CAP`) and adds a startup allowance to
 * the wait without charging it — see `lexiconMatchWorkerClient`. The uncapped
 * product let page 1 of a 5,000-entry apply consume the whole 30 s budget while
 * making the per-entry tier unreachable, so no entry could be named (BR-47).
 */
export const MATCH_BUDGET_ENTRY_PAGE_MS = 50;

/**
 * Ceiling on an entry's MEAN cost per page, in ms — tier 2.
 *
 * A RATE, and it has to be. This tier was a flat total (250 ms across the whole
 * document) charged from every page an entry ran on, healthy or not, with the
 * charge taken around the worker round trip. That makes it a budget on how long
 * the entry has EXISTED rather than on how expensive it is, and existence grows
 * with the document: the entry is abandoned after 250 ms of accumulated
 * round-trip time whatever the page count, so the pages it reaches is
 * `250 / cost-per-page` and the tail of a long document is unreachable by
 * construction. Measured on a 1,602-page book (~3,000 characters a page): a
 * single literal term — not a regex, nothing pathological — was abandoned
 * somewhere between page 100 and page 1,000 depending on machine speed, and
 * because it was the only entry the run then stopped altogether. Every
 * re-evaluation restarts at page 1 with a fresh budget, so re-applying died at
 * the same place and the rest of the book was never searched.
 *
 * The total also charged `elapsed / activeEntryCount`, so a ONE-entry lexicon
 * wore the whole round-trip cost while a fifty-entry one wore a fiftieth of it.
 * Small, simple lexicons were punished hardest, which is exactly backwards.
 *
 * A mean is scale-free: a literal term costing a fraction of a millisecond a
 * page never crosses it on a document of any length, and a pattern that is
 * genuinely expensive crosses it on page four as surely as on page four
 * hundred. It is set equal to `MATCH_BUDGET_ENTRY_PAGE_MS` on purpose — tier 1
 * is what one page may cost, and tier 2 says an entry may not AVERAGE more than
 * that.
 *
 * Bounding total work is not this tier's job and never really was: tier 1
 * terminates the worker mid-`RegExp.exec`, so a page's cost is capped whatever
 * the pattern does, and `MATCH_BUDGET_APPLY_MS` is the backstop on the whole
 * run. Tier 2's job is to stop re-running a known-expensive entry on every
 * remaining page, and to NAME it (BR-47).
 *
 * What a page charges (VE-29502): a page that reports charges each entry its
 * worker-measured compute time (`entryMs`), so main-thread stalls charge
 * nobody; a timeout charges the entries that did not report the page deadline;
 * a crash charges exactly this threshold. An entry that times out ALONE is
 * abandoned at once, whatever its mean.
 */
export const MATCH_BUDGET_ENTRY_MEAN_PAGE_MS = MATCH_BUDGET_ENTRY_PAGE_MS;

/**
 * Measured pages required before the tier-2 rate is trusted, per entry.
 *
 * A mean over one page is not a rate, it is a sample — and the first page an
 * entry runs on is the worst possible sample, because it carries regex compile
 * and JIT warm-up (and, without `entryMs`, worker module load and the first
 * structured clone). That one-off is why the client floors a
 * page's WAIT at `PAGE_STARTUP_ALLOWANCE_MS` without charging it; the same fact
 * means the first charged page is excluded from the mean entirely, and this is
 * how many pages after it must agree before an entry is abandoned.
 *
 * The cost of the sample is what a bad pattern may spend before it is caught:
 * at most `(1 + this) x` a page deadline, about 1.6 s, against a 30 s apply
 * budget. Cheap enough to be worth not abandoning a healthy entry over one slow
 * page.
 */
export const MATCH_RATE_MIN_PAGES = 3;

/**
 * Wall-clock budget for a whole apply operation, in ms.
 *
 * Whether a 5,000-entry lexicon (NFR-U5-7) fits depends on how many of its
 * terms appear per page: terms absent from a page are skipped cheaply
 * (`keywordPhraseMayMatch`), present ones pay the full scan. Measured on a
 * 1,602-page book (VE-29502): mostly-absent terms cover every page in ~11 s;
 * 5,000 common words run out at ~page 960.
 *
 * MATCHING IS NOT THE ONLY CLAIM ON IT, and this file is the record — ADR-001
 * has no file in the repo. A timed-out page whose lexicon has exactly one
 * ACTIVE entry costs more than its own deadline: `confirmLoneTimeout` refuses
 * to name a lone entry on a single observation (one timeout is equally a cold
 * worker start or a dead worker), so it sends an empty-entries liveness probe,
 * retried once because the probe races the very cold start
 * `PAGE_STARTUP_ALLOWANCE_MS` is sized for. Against a worker that never
 * answers, that moved the cost from two floor-length deadlines a page to
 * three (VE-29686) — so a doomed run now reaches ~25 pages of this budget
 * rather than ~37.
 *
 * The trade costs no disclosure: a dead worker names nobody either way, and
 * every page the run never reached is still reported as unsearched. It does
 * mean a second, non-matching consumer spends this budget — part of why
 * sizing it to the document is filed as VE-30159.
 */
export const MATCH_BUDGET_APPLY_MS = 30_000;

/** Max auto-retries for an append-only write that lost a revision race (BR-6). */
export const APPEND_CONFLICT_MAX_RETRIES = 3;

/**
 * Page sizes for the lexicons table (VE-24948 / U5).
 *
 * Verbatim from the prototype's shared settings-table shell: options
 * [10, 25, 50] with 25 selected. The repo-wide `DEFAULT_ROWS_PER_PAGE`
 * ([10, 20, 100]) cannot be used as-is — 25 is not one of its options, and MUI
 * needs the current value to be present in the list.
 */
export const LEXICON_ROWS_PER_PAGE = [10, 25, 50];
export const LEXICON_DEFAULT_PER_PAGE = 25;
